import type { PropType } from 'vue'
import type { DefaultError } from '@/types/httpError'
import type { CheckVideoLinkPayload, CheckVideoLinkResponse, IVideoLink } from '@/types/videoLink'
import { checkVideoLinkApiCall } from '@/api/videoLinks'
import { useApiCall } from '@/composables/useApiCall'
import { computed, defineComponent, ref, watch } from 'vue'

const PROVIDER_NAMES: Record<IVideoLink['provider'], string> = {
  vk: 'VK Видео',
  rutube: 'Rutube',
}

/**
 * Поле «Видео по ссылке»: ссылка на ролик VK Видео или Rutube, проверка у площадки и предпросмотр
 * (обложка, название, плеер). Сохраняет ссылку форма-родитель; сервер проверяет её ещё раз при сохранении.
 */
export default defineComponent({
  name: 'VideoLinkField',
  props: {
    value: { type: String, required: true },
    /** сохранённое видео записи — показывается, пока ссылку не меняли */
    link: { type: Object as PropType<IVideoLink | null>, required: false, default: null },
    title: { type: String, required: false, default: 'Видео по ссылке' },
    errorText: { type: Array as PropType<string[]>, required: false },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const checked = ref<IVideoLink | null>(props.link)
    const checkedFor = ref(props.link?.url ?? '')
    const checkError = ref('')
    const isPlayerShown = ref(false)

    const { data, executeApiCall, isLoading } = useApiCall<
      CheckVideoLinkResponse,
      DefaultError,
      CheckVideoLinkPayload
    >(checkVideoLinkApiCall, true)

    watch(
      () => props.link,
      (link) => {
        checked.value = link
        checkedFor.value = link?.url ?? ''
        isPlayerShown.value = false
      },
    )

    // Предпросмотр — только для той ссылки, что сейчас в поле.
    const preview = computed(() => {
      const url = props.value.trim()
      return checked.value && url !== '' && (checkedFor.value === url || checked.value.url === url)
        ? checked.value
        : null
    })
    const errors = computed(() => (checkError.value ? [checkError.value] : props.errorText))
    const providerName = computed(() => (preview.value ? PROVIDER_NAMES[preview.value.provider] : ''))
    const duration = computed(() => {
      const seconds = preview.value?.duration
      if (!seconds)
        return ''
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = String(seconds % 60).padStart(2, '0')
      return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
    })

    const onInput = (value: string) => {
      checkError.value = ''
      isPlayerShown.value = false
      emit('update:value', value)
    }

    const check = async () => {
      const url = props.value.trim()
      if (!url)
        return
      checkError.value = ''
      isPlayerShown.value = false
      try {
        await executeApiCall({ url })
        checked.value = data.value?.data ?? null
        checkedFor.value = url
      }
      catch (e) {
        const error = e as { data?: { error?: unknown, data?: Record<string, string[]> | null } } | null
        const fieldError = error?.data?.data?.url?.[0]
        const message = typeof error?.data?.error === 'string' ? error.data.error : ''
        checkError.value = fieldError || message || 'Не удалось проверить ссылку. Попробуйте ещё раз'
        checked.value = null
      }
    }

    const remove = () => {
      checked.value = null
      checkedFor.value = ''
      checkError.value = ''
      isPlayerShown.value = false
      emit('update:value', '')
    }

    return {
      preview,
      errors,
      providerName,
      duration,
      isLoading,
      isPlayerShown,
      onInput,
      check,
      remove,
    }
  },
})
